<template>
  <section v-if="isReady && reservations.length > 0" class="rent-reservations">
    <div class="rent-reservations-header">
      <h2 class="rent-reservations-title">Мои брони <span>({{ reservations.length }})</span></h2>
      <div class="rent-reservations-navigation">
        <button
          type="button"
          class="rent-reservations-navigation-button"
          :disabled="currentIndex === 0"
          aria-label="Предыдущая бронь"
          @click="showPrevious"
        >
          <img :src="arrowLeftIcon" alt="Предыдущая бронь" />
        </button>
        <button
          type="button"
          class="rent-reservations-navigation-button"
          :disabled="currentIndex === reservations.length - 1"
          aria-label="Следующая бронь"
          @click="showNext"
        >
          <img :src="arrowRightIcon" alt="Следующая бронь" />
        </button>
      </div>
    </div>

    <button
        v-if="currentReservation"
        type="button"
        class="rent-reservation-card"
        @click="openDetails(currentReservation.id)"
    >
      <span class="rent-reservation-card-main">
        <span class="rent-reservation-card-place">{{ currentReservation.schedule_data.place?.name || 'Зал' }}</span>
        <span class="rent-reservation-card-time">
          {{ formatTime(currentReservation.schedule_data.time_start) }} • {{ formatDate(currentReservation.schedule_data.date) }}
        </span>
      </span>
      <span class="rent-reservation-card-status">
        Аренда
      </span>
      <span v-if="trainerName(currentReservation)" class="rent-reservation-card-trainer">
        с тренером {{ trainerName(currentReservation) }}
      </span>
    </button>

    <div v-if="loading" class="rent-reservations-loader" aria-live="polite">
      <span class="rent-reservations-spinner"></span>
    </div>

    <div v-if="error" class="rent-reservations-empty">{{ error }}</div>

    <div v-if="selectedReservation" class="rent-modal-overlay" @click.self="closeDetails">
      <div class="rent-modal">
        <div class="rent-modal-header">
          <div class="rent-modal-header-spacer"></div>
          <h3 class="rent-modal-title">Детали бронирования</h3>
          <button type="button" class="rent-modal-close" @click="closeDetails">
            <img :src="closeIcon" alt="Закрыть" />
          </button>
        </div>

        <div v-if="detailsLoading" class="rent-modal-loader">
          <span class="rent-reservations-spinner"></span>
        </div>
        <template v-else>
          <div class="rent-modal-date-block">
            <strong>{{ formatDate(reservationDate) }}</strong>
            <span>{{ formatTime(reservationStart) }} - {{ formatTime(reservationEnd) }}</span>
            <div class="rent-modal-date-meta">
              <span class="rent-modal-date-meta-item">
                <BookingLocationIcon :size="16" :primary-color="bookingWidgetPrimaryColor" class="rent-modal-date-meta-icon" />
                {{ reservationPlaceName }}
              </span>
              <span class="rent-modal-date-meta-item">
                <BookingFlashIcon
                  :size="16"
                  :primary-color="bookingWidgetPrimaryColor"
                  :secondary-color="bookingWidgetPrimaryColor"
                  class="rent-modal-date-meta-icon"
                />
                {{ reservationDuration }}
              </span>
            </div>
          </div>

          <div class="rent-modal-place-block">
            <div class="rent-modal-place-name">{{ reservationPlaceName }}</div>
            <div class="rent-modal-place-description-wrap">
              <div class="rent-modal-place-label">Описание</div>
              <div class="rent-modal-place-description">{{ reservationDescription }}</div>
            </div>
          </div>

          <div class="rent-modal-payment-block">
            <span>{{ isPaid ? 'Оплачено' : 'К оплате' }}</span>
            <strong>{{ reservationAmount }}</strong>
          </div>

          <div v-if="reservationImages.length > 0" class="rent-modal-gallery">
            <div ref="galleryTrackRef" class="rent-modal-gallery-track">
              <img
                v-for="(image, index) in reservationImages"
                :key="`${selectedReservation.id}-${index}`"
                :src="image"
                :alt="`${reservationPlaceName} - фото ${index + 1}`"
                class="rent-modal-gallery-image"
              />
            </div>
            <div v-if="reservationImages.length > 1" class="rent-modal-gallery-controls">
              <button type="button" :disabled="currentImageIndex === 0" @click="scrollGallery('prev')">
                <img :src="arrowLeftIcon" alt="Предыдущее фото" />
              </button>
              <button
                type="button"
                :disabled="currentImageIndex === reservationImages.length - 1"
                @click="scrollGallery('next')"
              >
                <img :src="arrowRightIcon" alt="Следующее фото" />
              </button>
            </div>
          </div>

          <div v-if="detailsError" class="rent-modal-error">{{ detailsError }}</div>
          <div v-if="showLateCancelWarning" class="rent-modal-cancel-warning">
            До занятия осталось меньше {{ lateCancelLimitText }}, отменить его не получится
          </div>
          <button
            v-else-if="canCancel"
            type="button"
            class="rent-modal-cancel-button"
            :disabled="isCancelFlowActive"
            @click="startCancelFlow"
          >
            Отменить бронирование
          </button>
        </template>
      </div>
    </div>

    <div v-if="cancelFlowStep" class="rent-modal-overlay" @click.self="handleOverlayClick">
      <div class="rent-modal rent-cancel-flow-modal">
        <button
          v-if="cancelFlowStep !== 'loading'"
          type="button"
          class="rent-modal-close"
          @click="closeCancelFlow"
        >
          <img :src="closeIcon" alt="Закрыть" />
        </button>

        <template v-if="cancelFlowStep === 'confirm'">
          <img :src="infoIcon" alt="" class="rent-cancel-flow-icon" />
          <h3 class="rent-modal-title">Вы действительно хотите отменить бронирование?</h3>
          <p v-if="confirmText" class="rent-confirm-text">{{ confirmText }}</p>
          <div class="rent-confirm-actions rent-confirm-actions-row">
            <button type="button" class="rent-modal-cancel-button" @click="closeCancelFlow">Назад</button>
            <button type="button" class="rent-confirm-danger" @click="submitCancellation()">Да, отменить</button>
          </div>
        </template>

        <template v-else-if="cancelFlowStep === 'loading'">
          <h3 class="rent-modal-title">Отмена брони</h3>
          <div class="rent-cancel-flow-spinner-wrap">
            <span class="rent-reservations-spinner"></span>
          </div>
          <p class="rent-confirm-text">{{ loadingText }}</p>
        </template>

        <template v-else-if="cancelFlowStep === 'success'">
          <img :src="successIcon" alt="" class="rent-cancel-flow-icon" />
          <h3 class="rent-modal-title">Бронь отменена</h3>
          <p v-if="successText" class="rent-confirm-text">{{ successText }}</p>
          <div class="rent-confirm-actions">
            <button type="button" class="rent-confirm-primary" @click="closeCancelFlow">Готово</button>
          </div>
        </template>

        <template v-else-if="cancelFlowStep === 'error'">
          <img :src="dangerIcon" alt="" class="rent-cancel-flow-icon" />
          <h3 class="rent-modal-title">Произошла ошибка</h3>
          <p class="rent-confirm-text">{{ cancelErrorText }}</p>
          <div class="rent-confirm-actions">
            <button type="button" class="rent-confirm-primary" @click="closeCancelFlow">Готово</button>
          </div>
        </template>

        <template v-else-if="cancelFlowStep === 'late-cancel'">
          <img :src="infoIcon" alt="" class="rent-cancel-flow-icon" />
          <h3 class="rent-modal-title">Поздняя отмена</h3>
          <p class="rent-confirm-text">При отмене брони услуга будет списана. Вы уверены, что хотите продолжить?</p>
          <div class="rent-confirm-actions rent-confirm-actions-row">
            <button type="button" class="rent-modal-cancel-button" @click="closeCancelFlow">Оставить бронь</button>
            <button type="button" class="rent-confirm-danger" @click="submitCancellation">Отменить бронь</button>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
/* global defineExpose, defineProps */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { BookingAPI } from '@/widgets/booking/api'
import { getStoredAuthToken } from '@/widgets/booking/config/runtime'
import { formatCurrencyPrice } from '@/widgets/booking/utils/currency'
import arrowLeftIcon from '@/widgets/booking/assets/img/icons/arrow_left.svg'
import arrowRightIcon from '@/widgets/booking/assets/img/icons/arrow_right.svg'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import infoIcon from '@/assets/img/info.svg'
import successIcon from '@/assets/img/success.svg'
import dangerIcon from '@/assets/img/danger.svg'
import BookingLocationIcon from '@/widgets/booking/components/icons/BookingLocationIcon.vue'
import BookingFlashIcon from '@/widgets/booking/components/icons/BookingFlashIcon.vue'

const props = defineProps({
  isReady: { type: Boolean, default: false },
  currency: { type: String, default: 'RUB' },
  primaryColor: { type: String, default: '#3C67D6' }
})

const reservations = ref([])
const currentIndex = ref(0)
const selectedReservation = ref(null)
const loading = ref(false)
const detailsLoading = ref(false)
const cancelFlowStep = ref(null)
const error = ref('')
const detailsError = ref('')
const cancellationResult = ref(null)
const currentImageIndex = ref(0)
const galleryTrackRef = ref(null)
const bookingWidgetPrimaryColor = computed(() => props.primaryColor || '#3C67D6')
const cancellationRules = computed(() => selectedReservation.value?.cancellation_rules || {})
const isPaid = computed(() => (
  selectedReservation.value?.is_paid === true || Number(selectedReservation.value?.is_paid) === 1
))
const isLateRentCancellation = computed(() => (
  cancellationRules.value.type === 'late'
  || cancellationRules.value.consequence === 'service_writeoff'
))
const hasRefundOnCancellation = computed(() => cancellationRules.value.consequence === 'refund')
const requiresClubRefund = computed(() => cancellationRules.value.consequence === 'refund_in_club')
const cancellationErrorMessage = computed(() => (
  hasRefundOnCancellation.value
    ? 'Во время отмены брони и возврата средств произошла ошибка. Пожалуйста, обратитесь к\u00A0администратору клуба'
    : 'Во время отмены брони произошла ошибка. Пожалуйста, обратитесь к\u00A0администратору клуба'
))
const cancelErrorText = ref(cancellationErrorMessage.value)

const isCancelFlowActive = computed(() => cancelFlowStep.value !== null)
const cancellationRefund = computed(() => cancellationResult.value?.refund || null)
const isOnlineRefundCompleted = computed(() => (
  cancellationRefund.value?.type === 'online' && cancellationRefund.value?.status === 'completed'
))
const confirmText = computed(() => {
  if (isLateRentCancellation.value) return ''
  if (requiresClubRefund.value) return 'За возвратом денежных средств обратитесь в клуб'
  return hasRefundOnCancellation.value
    ? 'После отмены брони деньги поступят на счет, с\u00A0которого была произведена оплата'
    : ''
})
const loadingText = computed(() => (
  hasRefundOnCancellation.value
    ? 'Бронь отменяется. Производим возврат средств'
    : 'Бронь отменяется'
))
const successText = computed(() => {
  if (isLateRentCancellation.value) return ''
  if (requiresClubRefund.value) return 'За возвратом денежных средств обратитесь в клуб'
  if (isOnlineRefundCompleted.value) {
    return 'Возврат средств успешно выполнен. Деньги поступят на карту, с которой была произведена оплата'
  }
  if (cancellationRefund.value?.type === 'offline') {
    return 'За возвратом денежных средств обратитесь в клуб'
  }
  return ''
})

const currentReservation = computed(() => reservations.value[currentIndex.value] || null)
const canCancel = computed(() => {
  if (!selectedReservation.value) return false
  const status = selectedReservation.value.status || reservationSchedule.value.status
  const date = reservationSchedule.value.date
  const start = reservationSchedule.value.time_start || reservationSchedule.value.start
  return ![2, 5].includes(Number(status)) && date && start
    && new Date(`${date}T${start}`) > new Date()
})
const isLateCancelForbidden = computed(() => cancellationRules.value.is_late_cancel_forbidden === true)
const showLateCancelWarning = computed(() => canCancel.value && isLateCancelForbidden.value)
const lateCancelLimitText = computed(() => formatLateCancelLimit(cancellationRules.value.late_cancel_time_minutes))
const reservationSchedule = computed(() => selectedReservation.value?.schedule_data || selectedReservation.value || {})
const reservationDate = computed(() => reservationSchedule.value.date || '')
const reservationStart = computed(() => reservationSchedule.value.time_start || reservationSchedule.value.start || '')
const reservationEnd = computed(() => reservationSchedule.value.time_end || reservationSchedule.value.end || '')
const reservationPlaceName = computed(() => (
  reservationSchedule.value.place?.name
  || reservationSchedule.value.place_name
  || selectedReservation.value?.place_name
  || 'Зал'
))
const reservationImages = computed(() => {
  const placeDetailsImages = selectedReservation.value?.place_details?.images
  if (Array.isArray(placeDetailsImages) && placeDetailsImages.length > 0) {
    return placeDetailsImages.filter(Boolean)
  }
  const place = reservationSchedule.value.place || selectedReservation.value?.place || {}
  const images = place.images || reservationSchedule.value.images || selectedReservation.value?.images || []
  if (Array.isArray(images)) return images.filter(Boolean)
  return reservationSchedule.value.image || selectedReservation.value?.image
    ? [reservationSchedule.value.image || selectedReservation.value.image]
    : []
})
const reservationAmount = computed(() => {
  const amount = selectedReservation.value?.price_to_pay
    || selectedReservation.value?.amount
    || reservationSchedule.value.price_to_pay
    || reservationSchedule.value.amount
  const currency = typeof amount === 'object' ? amount.currency : props.currency
  return formatCurrencyPrice(amount, currency || props.currency)
})
const reservationDuration = computed(() => {
  const start = reservationStart.value
  const end = reservationEnd.value
  if (!start || !end) return ''
  const [startHours, startMinutes] = start.slice(0, 5).split(':').map(Number)
  const [endHours, endMinutes] = end.slice(0, 5).split(':').map(Number)
  const duration = ((endHours * 60) + endMinutes) - ((startHours * 60) + startMinutes)
  return duration > 0 ? `${duration} мин` : ''
})
const reservationDescription = computed(() => (
  selectedReservation.value?.place_details?.description
  || reservationSchedule.value.place?.description
  || selectedReservation.value?.place?.description
  || reservationSchedule.value.description
  || selectedReservation.value?.description
  || 'Описание отсутствует'
))

async function loadReservations() {
  if (!props.isReady || !getStoredAuthToken()) {
    reservations.value = []
    return
  }
  loading.value = true
  error.value = ''
  reservations.value = await BookingAPI.getRentReservations()
  currentIndex.value = Math.min(currentIndex.value, Math.max(reservations.value.length - 1, 0))
  loading.value = false
}

async function openDetails(id) {
  detailsLoading.value = true
  detailsError.value = ''
  currentImageIndex.value = 0
  selectedReservation.value = { id }
  const result = await BookingAPI.getReservation(id)
  if (result.success) {
    selectedReservation.value = result.data
    await nextTick()
    syncGalleryPosition()
  }
  else detailsError.value = result.error
  detailsLoading.value = false
}

function closeDetails() {
  if (!isCancelFlowActive.value) selectedReservation.value = null
}

function syncGalleryPosition() {
  const gallery = galleryTrackRef.value
  if (!gallery) return
  const firstImage = gallery.querySelector('.rent-modal-gallery-image')
  if (!firstImage) return
  const gap = 8
  const scrollAmount = firstImage.clientWidth + gap
  gallery.scrollTo({ left: scrollAmount * currentImageIndex.value, behavior: 'smooth' })
}

function scrollGallery(direction) {
  if (direction === 'prev' && currentImageIndex.value > 0) {
    currentImageIndex.value -= 1
    syncGalleryPosition()
  }

  if (direction === 'next' && currentImageIndex.value < reservationImages.value.length - 1) {
    currentImageIndex.value += 1
    syncGalleryPosition()
  }
}

async function startCancelFlow() {
  cancellationResult.value = null
  cancelErrorText.value = cancellationErrorMessage.value

  const reservationId = selectedReservation.value?.id
  if (!reservationId) return

  const result = await BookingAPI.getReservation(reservationId)
  if (!result.success) {
    cancelErrorText.value = result.error || cancellationErrorMessage.value
    cancelFlowStep.value = 'error'
    return
  }

  selectedReservation.value = result.data
  if (selectedReservation.value?.cancellation_rules?.is_late_cancel_forbidden === true) {
    return
  }

  cancelFlowStep.value = selectedReservation.value?.cancellation_rules?.requires_confirmation === true
    ? 'late-cancel'
    : 'confirm'
}

function closeCancelFlow() {
  if (cancelFlowStep.value === 'loading') return
  const wasSuccess = cancelFlowStep.value === 'success'
  cancelFlowStep.value = null
  if (wasSuccess) {
    selectedReservation.value = null
    loadReservations()
  }
}

function handleOverlayClick() {
  closeCancelFlow()
}

async function submitCancellation() {
  if (!selectedReservation.value?.id) return
  cancelFlowStep.value = 'loading'
  const result = await BookingAPI.cancelReservation(selectedReservation.value.id)
  if (!result.success && result.status !== 'success') {
    cancelErrorText.value = cancellationErrorMessage.value
    cancelFlowStep.value = 'error'
    return
  }
  cancellationResult.value = result
  cancelFlowStep.value = 'success'
}

function showPrevious() { currentIndex.value = Math.max(currentIndex.value - 1, 0) }
function showNext() { currentIndex.value = Math.min(currentIndex.value + 1, reservations.value.length - 1) }
function trainerName(reservation) {
  const trainer = reservation.schedule_data.trainer || reservation.schedule_data.trainer_replacement
  if (!trainer) return ''
  return trainer.short_fio || trainer.name || trainer.fio || ''
}
function formatDate(value) {
  if (!value) return ''
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' })
}
function formatTime(value) { return value ? String(value).slice(0, 5) : '' }
function formatLateCancelLimit(minutes) {
  const value = Number(minutes)
  if (!Number.isFinite(value) || value <= 0) return '0 минут'
  if (value % 60 !== 0) return `${value} ${pluralize(value, ['минуту', 'минуты', 'минут'])}`
  const hours = value / 60
  return `${hours} ${pluralize(hours, ['час', 'часа', 'часов'])}`
}
function pluralize(value, forms) {
  const absolute = Math.abs(value) % 100
  const lastDigit = absolute % 10
  if (absolute > 10 && absolute < 20) return forms[2]
  if (lastDigit > 1 && lastDigit < 5) return forms[1]
  if (lastDigit === 1) return forms[0]
  return forms[2]
}

watch(() => props.isReady, (ready) => {
  if (ready) loadReservations()
  else reservations.value = []
})

defineExpose({ reload: loadReservations })
onMounted(() => {
  if (props.isReady) loadReservations()
})
</script>

<style scoped lang="scss">
@import '@/widgets/booking/assets/scss/variables.scss';

.rent-reservations { margin: 16px 0; }
.rent-reservations-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.rent-reservations-title { color: #333; font-size: 16px; font-weight: 700; }
.rent-reservations-title span { color: #757885; font-weight: 400; }
.rent-reservations-navigation { display: flex; gap: 8px; }
.rent-reservations-navigation-button { width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: #F3F5F8; }
.rent-reservations-navigation-button img { width: 12px; height: 12px; filter: var(--booking-widget-primary-icon-filter, none); }
.rent-reservations-navigation-button:disabled { opacity: .45; cursor: default; }
.rent-reservation-card { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px; width: 100%; padding: 16px; border-radius: 12px; background: #F3F5F8; color: #333; text-align: left; }
.rent-reservation-card-main { display: grid; gap: 4px; min-width: 0; }
.rent-reservation-card-place { font-size: 16px; font-weight: 700; }
.rent-reservation-card-time, .rent-reservation-card-trainer { color: #757885; font-size: 14px; }
.rent-reservation-card-status { height: 24px; display: flex; align-items: center; padding: 4px 8px; border-radius: 12px; background: #4EB410; color: #fff; font-size: 12px; white-space: nowrap; }
.rent-reservation-card-trainer { grid-column: 1 / -1; }
.rent-reservations-loader { display: flex; justify-content: center; padding: 12px; }
.rent-reservations-empty { color: #ED3D3D; font-size: 14px; }
.rent-reservations-spinner { width: 24px; height: 24px; display: inline-block; border: 3px solid #DDE3F5; border-top-color: var(--booking-widget-primary-color, #3C67D6); border-radius: 50%; animation: rent-spin .7s linear infinite; }
.rent-modal-overlay { position: fixed; inset: 0; z-index: 1700; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(0,0,0,.5); }
.rent-modal { width: 100%; max-width: 480px; max-height: calc(100vh - 40px); display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding: 24px; border-radius: 30px; background: #fff; }
.rent-modal-header { display: grid; grid-template-columns: 24px minmax(0, 1fr) 24px; align-items: center; column-gap: 12px; }
.rent-modal-header-spacer { width: 24px; height: 24px; }
.rent-modal-close { width: 24px; height: 24px; padding: 5px; border-radius: 7px; background: #F3F5F8; }
.rent-modal-close img { width: 100%; height: 100%; }
.rent-modal-title { margin: 0; font-size: 16px; font-weight: 700; text-align: center; }
.rent-modal-date-block, .rent-modal-place-block, .rent-modal-payment-block { display: grid; gap: 12px; padding: 16px; border-radius: 12px; background: #F3F5F8; }
.rent-modal-date-block strong { font-size: 20px; text-transform: capitalize; color: #333333; }
.rent-modal-date-block span { color: #757885; font-size: 16px; }
.rent-modal-date-meta { display: flex; justify-content: space-between; gap: 16px; margin-top: 4px; color: #757885; font-size: 14px; }
.rent-modal-date-meta-item { display: inline-flex; align-items: center; gap: 6px; }
.rent-modal-date-meta-icon { width: 16px; height: 16px; flex-shrink: 0; filter: var(--booking-widget-primary-icon-filter, none); }
.rent-modal-place-block { gap: 12px; }
.rent-modal-place-name { color: #333333; font-size: 16px; font-weight: 700; }
.rent-modal-place-description-wrap { display: grid; gap: 8px; }
.rent-modal-place-label { color: #757885; font-size: 12px; }
.rent-modal-place-description { color: #333333; font-size: 12px; line-height: 18px; }
.rent-modal-payment-block { display: flex; align-items: center; justify-content: space-between; }
.rent-modal-payment-block span { color: #333333; font-size: 16px; }
.rent-modal-payment-block strong { color: #333333; font-size: 16px; }
.rent-modal-gallery { display: flex; flex-direction: column; gap: 12px; }
.rent-modal-gallery-track { display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; }
.rent-modal-gallery-track::-webkit-scrollbar { display: none; }
.rent-modal-gallery-image { width: 210px; min-width: 210px; height: 148px; object-fit: cover; border-radius: 16px; background: #F3F5F8; }
.rent-modal-gallery-controls { display: flex; justify-content: flex-start; gap: 8px; }
.rent-modal-gallery-controls button { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: #F3F5F8; }
.rent-modal-gallery-controls button img { width: 12px; height: 12px; }
.rent-modal-gallery-controls button:disabled { opacity: .45; }
.rent-modal-error { margin-top: 12px; color: #ED3D3D; font-size: 14px; }
.rent-modal-cancel-warning { padding: 12px 14px; border-radius: 8px; background: #F198191A; color: #F19819; font-size: 14px; line-height: 20px; font-weight: 400; }
.rent-modal-cancel-button, .rent-confirm-primary, .rent-confirm-secondary, .rent-confirm-danger { width: 100%; min-height: 44px; margin-top: auto; border-radius: 8px; font-weight: 700; }
.rent-modal-cancel-button { background: var(--booking-widget-primary-color-soft, #3C67D61A); color: var(--booking-widget-primary-color, #3C67D6); }
.rent-confirm-primary { background: var(--booking-widget-primary-color, #3C67D6); color: #fff; }
.rent-confirm-secondary { margin-top: 0; background: var(--booking-widget-primary-color-soft, #3C67D61A); color: var(--booking-widget-primary-color, #3C67D6); }
.rent-confirm-danger { background: #F15F941A; color: #ED3D3D; }
.rent-confirm-text { margin: 0; color: #6F7480; font-size: 14px; line-height: 22px; text-align: center; }
.rent-confirm-actions { display: grid; gap: 8px; margin-top: 8px; }
.rent-confirm-actions-row { display: flex; flex-direction: row; gap: 8px; }
.rent-confirm-actions-row > button { flex: 1; }
.rent-cancel-flow-modal { max-width: 360px; gap: 16px; padding: 24px; position: relative; }
.rent-cancel-flow-modal .rent-modal-close { position: absolute; top: 16px; right: 16px; }
.rent-cancel-flow-modal .rent-modal-title { padding: 0 24px; }
.rent-cancel-flow-modal .rent-confirm-primary,
.rent-cancel-flow-modal .rent-confirm-secondary,
.rent-cancel-flow-modal .rent-confirm-danger,
.rent-cancel-flow-modal .rent-modal-cancel-button { margin-top: 0; }
.rent-cancel-flow-icon { width: 48px; height: 48px; display: block; margin: 0 auto; }
.rent-cancel-flow-spinner-wrap { display: flex; justify-content: center; padding: 4px 0; }
.rent-cancel-flow-spinner-wrap .rent-reservations-spinner { width: 40px; height: 40px; border-width: 4px; }
@keyframes rent-spin { to { transform: rotate(360deg); } }
</style>
